import { Bullet, Callout, LegalPage, Mail, P, Section, Strong } from '@/components/legal-page';
import { LEGAL_UPDATED, SITE_NAME } from '@/constants/site';

/**
 * Terms of use. Deliberately short and readable — the app is a free game with no
 * accounts, no purchases, and no user-generated content, so most of a standard
 * ToS would describe machinery that doesn't exist.
 *
 * No governing-law clause: picking a jurisdiction is the owner's call, not a
 * default worth guessing at. Add one here before any commercial use.
 */
export default function TermsScreen() {
  return (
    <LegalPage
      kicker="LEGAL"
      title="Terms of Use"
      subtitle={`Last updated ${LEGAL_UPDATED}`}
      description={`The terms for using ${SITE_NAME} — a free satirical game. Valuations are fictional and nothing here is advice.`}
      current="/terms">
      <Callout>
        {SITE_NAME} is a satirical game. Every valuation, score, and verdict it produces is invented
        for entertainment and describes nothing real.
      </Callout>

      <Section title="The deal">
        <P>
          {SITE_NAME} is offered free of charge for personal entertainment. By using the app or the
          website you agree to these terms. If you don&apos;t, please don&apos;t use it.
        </P>
      </Section>

      <Section title="It’s a game, not advice">
        <P>
          The valuations, chemistry scores, and commentary are generated by a scoring model built
          for laughs. They are fiction.
        </P>
        <Bullet>
          Nothing in the app is <Strong>financial, investment, business, hiring, or career advice</Strong>,
          and none of it should inform a real decision about any of those things.
        </Bullet>
        <Bullet>
          No result predicts, estimates, or reports the actual performance of any company, fund, or
          person.
        </Bullet>
      </Section>

      <Section title="Real people, invented outcomes">
        <P>
          The roster depicts well-known public figures from the technology industry. Their names and
          likenesses appear as commentary, parody, and satire on public business culture.
        </P>
        <Bullet>
          Every outcome, quote, pairing, and verdict is <Strong>fabricated by the game</Strong>. None
          of it is a statement of fact about any real person or company.
        </Bullet>
        <Bullet>
          No endorsement, sponsorship, or affiliation is claimed or implied by anyone depicted.
        </Bullet>
        <Bullet>
          If you are depicted here and would rather not be, write to <Mail subject="Roster removal request" />{' '}
          and you will be removed from the roster in the next release.
        </Bullet>
      </Section>

      <Section title="What you may do">
        <P>
          Play as much as you like, screenshot your results, and share them anywhere. That&apos;s
          what the share button is for.
        </P>
      </Section>

      <Section title="What you may not do">
        <Bullet>
          Present anything the game generates as a factual claim about a real person or company.
        </Bullet>
        <Bullet>Use the app, or results from it, to harass, defame, or mislead anyone.</Bullet>
        <Bullet>Resell, repackage, or redistribute the app or its assets as your own.</Bullet>
        <Bullet>
          Reverse engineer, attack, or otherwise interfere with the app or the service that delivers
          it.
        </Bullet>
      </Section>

      <Section title="Intellectual property">
        <P>
          The {SITE_NAME} name, design, artwork, copy, and scoring model belong to their author.
          Public figures&apos; names remain their own; they are used here descriptively and
          satirically, not as trademarks of this app.
        </P>
      </Section>

      <Section title="No warranty">
        <P>
          The app and website are provided &ldquo;as is,&rdquo; without warranties of any kind to the
          fullest extent permitted by law. There is no guarantee that they will be available,
          uninterrupted, error-free, or preserved in any particular form.
        </P>
      </Section>

      <Section title="Limitation of liability">
        <P>
          To the fullest extent permitted by law, the author is not liable for any indirect,
          incidental, or consequential damages arising from your use of {SITE_NAME}. It is a free
          game about drafting a fake company; please treat it as one.
        </P>
      </Section>

      <Section title="Changes">
        <P>
          The app, its roster, and these terms may change at any time, and the app may be withdrawn
          entirely. Continuing to use it after a change means you accept the updated terms; the date
          at the top always reflects the current version.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions about these terms: <Mail subject="Terms question" />.
        </P>
      </Section>
    </LegalPage>
  );
}
